// The check an arbiter runs before they export, report or publish: everything about this
// tournament that a person downstream will reject, ask about, or quietly get wrong.
//
// WHY A CHECK AND NOT A SET OF GUARDS. Almost nothing here can be forbidden at the moment
// it happens. A player legitimately has no FIDE ID until someone looks it up; a round
// legitimately has empty results for the hour it is being played; a colour imbalance of
// three is not illegal, it is unusual and worth a second look. Refusing any of it would
// stop an arbiter from running their tournament. Listing all of it, at the moment they
// are about to hand the file to someone else, is the useful shape.
//
// LEVELS MEAN WHAT THEY SAY.
//   error    the file or the report will be wrong, or be rejected. Fix before sending.
//   warning  probably a mistake, and cheap to check. Worth a look.
//   info     a fact worth knowing, not a fault. An unrated player is not a problem.
//
// MESSAGES ARE TEMPLATES, not sentences. Every finding carries a translation key and its
// parameters, the same contract Acceleration.describe and the pairing refusals use, so
// the Norwegian dictionary gets whole sentences rather than fragments spliced together.
//
// Pure: no DOM, no state, no Firestore, and no dependency on the pairing engines — the
// colour and meeting history below is recomputed here from the rounds rather than read
// from SwissEngine, so this runs under node:test with nothing installed.

import { isYouthTournament } from './player-ratings.js';
import { playerBirthYear } from './formatting.js';

const LEVELS = ['error', 'warning', 'info'];

// Colour imbalance beyond this is flagged. Two is the Dutch system's own limit: rule
// C.04.3 forbids a difference above ±2 except in the last round, so a field with a player
// at three is either mid-repair or has had a round edited by hand.
const MAX_COLOUR_DIFFERENCE = 2;

// Three of the same colour in a row is the other half of the same rule.
const MAX_SAME_COLOUR_RUN = 2;

const SPECIAL = new Set(['WO', 'BYE']);
const isReal = id => !!id && !SPECIAL.has(id);

// A forfeited game was never played — the same rule the pairing engines use (see
// isPlayedGame in js/core/swiss-engine.js), so "these two have met" means the same thing
// here as it does there.
const UNPLAYED_RESULTS = new Set(['+/-', '-/+', '-/-']);

const ROUND_ROBIN_FORMATS = new Set([
    'rr', 'drr', 'round-robin', 'roundrobin', 'double-rr', 'double-round-robin'
]);

function finding(level, code, key, params, players) {
    return { level, code, message: { key, params: params || {} }, players: players || [] };
}

/**
 * Pick the singular or the plural wording for a count.
 *
 * Most findings here avoid the problem by listing the players instead of counting them —
 * the names are the useful part, and the count was only ever a number in front of them.
 * The three that genuinely have nothing to list still have to agree with their noun, and
 * "1 spillere" is visibly wrong Norwegian. The same trick MonradEngine's round-count
 * message uses: choose the key, do not assemble the sentence.
 */
function plural(count, one, many) {
    return count === 1 ? one : many;
}

/** Player history: colours played, opponents met, and full-point byes allocated. */
function historyOf(players, rounds) {
    const history = {};
    players.forEach(p => {
        history[p.id] = { colours: [], met: new Map(), fullByes: 0 };
    });

    (rounds || []).forEach(round => {
        (round.pairings || []).forEach(pair => {
            const white = pair.white;
            const black = pair.black;
            const result = pair.result || '';

            // A bye: one real player, no opponent. A full point for one that the arbiter
            // did not record as requested is the pairing-allocated bye, and a player may
            // only ever be given one of those.
            if (isReal(white) && !isReal(black)) {
                if (!history[white]) return;
                if (!pair.requestedBye && (result === '1-0' || result === '+/-')) history[white].fullByes++;
                return;
            }
            if (isReal(black) && !isReal(white)) {
                if (!history[black]) return;
                if (!pair.requestedBye && (result === '0-1' || result === '-/+')) history[black].fullByes++;
                return;
            }
            if (!isReal(white) || !isReal(black)) return;
            if (!history[white] || !history[black]) return;

            history[white].colours.push('w');
            history[black].colours.push('b');

            if (!UNPLAYED_RESULTS.has(result)) {
                history[white].met.set(black, (history[white].met.get(black) || 0) + 1);
                history[black].met.set(white, (history[black].met.get(white) || 0) + 1);
            }
        });
    });
    return history;
}

function longestRun(colours) {
    let best = 0;
    let run = 0;
    let previous = null;
    colours.forEach(c => {
        run = c === previous ? run + 1 : 1;
        previous = c;
        if (run > best) best = run;
    });
    return best;
}

/**
 * Check a tournament.
 *
 * @param {Object} opts
 * @param {Array}  opts.players
 * @param {Array}  opts.rounds
 * @param {Object} opts.settings    getActiveSettings()'s shape
 * @param {Object} [opts.tournament] the tournament record, for the fields a TRF needs
 *                                   that are not settings (location, dates, arbiter)
 * @param {number} [opts.maxMeetings] how often a pair may meet; Konrad allows more than one
 * @returns {{ findings: Array, counts: { error, warning, info } }}
 */
export function checkTournament({ players, rounds, settings, tournament, maxMeetings } = {}) {
    players = players || [];
    rounds = rounds || [];
    settings = settings || {};
    const t = tournament || {};
    const findings = [];

    const format = String(settings.format || 'swiss').toLowerCase();
    const isRoundRobin = ROUND_ROBIN_FORMATS.has(format);
    const allowance = Math.max(1, parseInt(maxMeetings, 10) || 1);
    const label = p => `${p.lastName || ''}, ${p.givenName || ''}`.replace(/^, |, $/g, '').trim() || `#${p.startNo}`;
    const names = list => list.map(label).sort((a, b) => a.localeCompare(b));

    // ---- the field ----------------------------------------------------------

    if (players.length === 0) {
        findings.push(finding('error', 'no-players', 'There are no players in this tournament.'));
        // Everything below describes the field, so there is nothing more to say.
        return summarise(findings);
    }

    const missingStartNo = players.filter(p => !(parseInt(p.startNo, 10) > 0));
    if (missingStartNo.length) {
        findings.push(finding('error', 'missing-start-no',
            'Players with no starting number: {names}',
            { names: names(missingStartNo).join(', ') },
            missingStartNo.map(p => p.id)));
    }

    const byNumber = new Map();
    players.forEach(p => {
        const n = parseInt(p.startNo, 10);
        if (!(n > 0)) return;
        if (!byNumber.has(n)) byNumber.set(n, []);
        byNumber.get(n).push(p);
    });
    const duplicated = [...byNumber.entries()].filter(([, list]) => list.length > 1);
    if (duplicated.length) {
        findings.push(finding('error', 'duplicate-start-no',
            'Starting number {numbers} is used by more than one player.',
            { numbers: duplicated.map(([n]) => n).join(', ') },
            duplicated.flatMap(([, list]) => list.map(p => p.id))));
    }

    // ---- results ------------------------------------------------------------

    rounds.forEach((round, i) => {
        const unfinished = (round.pairings || []).filter(pair =>
            !pair.result && isReal(pair.white) && isReal(pair.black));
        if (unfinished.length) {
            findings.push(finding('error', 'missing-results',
                plural(unfinished.length,
                    'Round {round} has one game with no result.',
                    'Round {round} has {count} games with no result.'),
                { round: i + 1, count: unfinished.length }));
        }
    });

    const configuredRounds = parseInt(settings.roundsCount, 10) || 0;
    if (configuredRounds > 0 && rounds.length > configuredRounds) {
        findings.push(finding('warning', 'rounds-exceed-configured',
            '{played} rounds have been played, but the tournament is set to {configured}.',
            { played: rounds.length, configured: configuredRounds }));
    }

    // ---- pairing history ----------------------------------------------------

    const history = historyOf(players, rounds);
    const playerById = {};
    players.forEach(p => { playerById[p.id] = p; });

    if (!isRoundRobin) {
        const repeats = [];
        players.forEach(p => {
            const h = history[p.id];
            if (!h) return;
            h.met.forEach((count, otherId) => {
                // Each pair is reported once: only from the side with the smaller id.
                if (count > allowance && String(p.id) < String(otherId) && playerById[otherId]) {
                    repeats.push(`${label(p)} – ${label(playerById[otherId])}`);
                }
            });
        });
        if (repeats.length) {
            findings.push(finding('error', 'repeat-pairing',
                allowance > 1
                    ? 'Pairs that have met more than {allowance} times: {pairs}'
                    : 'Pairs that have played each other twice: {pairs}',
                { allowance, pairs: repeats.sort().join('; ') }));
        }
    }

    const imbalanced = players.filter(p => {
        const colours = (history[p.id] || {}).colours || [];
        const whites = colours.filter(c => c === 'w').length;
        return Math.abs(whites * 2 - colours.length) > MAX_COLOUR_DIFFERENCE;
    });
    if (imbalanced.length) {
        findings.push(finding('warning', 'colour-imbalance',
            'Players whose colours are more than {max} apart: {names}',
            { max: MAX_COLOUR_DIFFERENCE, names: names(imbalanced).join(', ') },
            imbalanced.map(p => p.id)));
    }

    const streaked = players.filter(p =>
        longestRun((history[p.id] || {}).colours || []) > MAX_SAME_COLOUR_RUN);
    if (streaked.length) {
        findings.push(finding('warning', 'colour-streak',
            'Players who have had the same colour more than {max} times in a row: {names}',
            { max: MAX_SAME_COLOUR_RUN, names: names(streaked).join(', ') },
            streaked.map(p => p.id)));
    }

    const doubleByes = players.filter(p => ((history[p.id] || {}).fullByes || 0) > 1);
    if (doubleByes.length) {
        findings.push(finding('warning', 'repeat-full-bye',
            'Players given the full-point bye more than once: {names}',
            { names: names(doubleByes).join(', ') },
            doubleByes.map(p => p.id)));
    }

    // ---- what a report needs about each player ------------------------------

    const rated = !isYouthTournament(settings);

    const unrated = players.filter(p => !(parseInt(p.rating, 10) > 0));
    if (unrated.length) {
        findings.push(finding('info', 'unrated-players',
            plural(unrated.length, 'One player has no rating.', '{count} players have no rating.'),
            { count: unrated.length },
            unrated.map(p => p.id)));
    }

    if (rated) {
        const noFideId = players.filter(p => !String(p.fideId || '').trim());
        if (noFideId.length) {
            findings.push(finding('warning', 'missing-fide-id',
                'Players with no FIDE ID, which a rating report needs: {names}',
                { names: names(noFideId).join(', ') },
                noFideId.map(p => p.id)));
        }

        const noFederation = players.filter(p => !String(p.fed || '').trim());
        if (noFederation.length) {
            findings.push(finding('warning', 'missing-federation',
                plural(noFederation.length,
                    'One player has no federation.',
                    '{count} players have no federation.'),
                { count: noFederation.length },
                noFederation.map(p => p.id)));
        }
    }

    const noBirthYear = players.filter(p => !playerBirthYear(p));
    if (noBirthYear.length) {
        findings.push(finding('warning', 'missing-birth-year',
            'Players with no birth year, so they fall into no age category: {names}',
            { names: names(noBirthYear).join(', ') },
            noBirthYear.map(p => p.id)));
    }

    // ---- what a report needs about the tournament ---------------------------

    if (!String(t.chiefArbiter || t.arbiterName || '').trim()) {
        findings.push(finding('warning', 'missing-arbiter',
            'No chief arbiter is named, and a tournament report identifies who ran the event.'));
    }
    if (!String(t.location || '').trim()) {
        findings.push(finding('warning', 'missing-location', 'The tournament has no location.'));
    }
    if (!String(t.startDate || '').trim()) {
        findings.push(finding('warning', 'missing-dates', 'The tournament has no start date.'));
    }
    if (!String(t.timeControl || '').trim()) {
        findings.push(finding('warning', 'missing-time-control',
            'No time control is set, and a rating report needs one to know which list the games belong to.'));
    }
    if (!Array.isArray(settings.tiebreaks) || settings.tiebreaks.length === 0) {
        findings.push(finding('warning', 'no-tiebreaks',
            'No tie-breaks are selected, so players on the same score are not separated.'));
    }

    // ---- the timetable ------------------------------------------------------

    const schedule = Array.isArray(settings.roundSchedule) ? settings.roundSchedule : [];
    const scheduled = schedule.filter(e => e && e.date).length;
    if (configuredRounds > 0 && scheduled > 0 && scheduled < configuredRounds) {
        findings.push(finding('info', 'schedule-incomplete',
            '{scheduled} of {total} rounds have a date. The rest are not shown to players.',
            { scheduled, total: configuredRounds }));
    }

    return summarise(findings);
}

function summarise(findings) {
    const counts = { error: 0, warning: 0, info: 0 };
    findings.forEach(f => { counts[f.level] = (counts[f.level] || 0) + 1; });
    const order = f => LEVELS.indexOf(f.level);
    return { findings: findings.slice().sort((a, b) => order(a) - order(b)), counts };
}
